import { useEffect, useRef, useState } from "react";
import { PinFace, pinClass, pinStyle } from "@/threedvista/pinStyles";
import { PanoStage } from "@/threedvista/PanoStage";
import { tdvOf, scenesOf, currentIndex, panoPlayer, setNativeHotspots, project, unproject } from "@/threedvista/bridge";

const viewParam = (key) => {
  const m = /^CAM0*(.+?)-PR0*(\d+)-PAN0*(\d+)$/i.exec(key);
  return m ? btoa(`${m[1]}-${m[2]}-${m[3]},0,0`).replace(/\+/g, "-").replace(/\//g, "_") : encodeURIComponent(key);
};
const withView = (url, key) => (key ? `${url}${url.includes("?") ? "&" : "?"}v=${viewParam(key)}` : url);
const fromGiraffe = (o) => { try { return /(^|\.)giraffe360\.com$/.test(new URL(o).hostname); } catch { return false; } };

function GiraffeStage({ src, giraffe, onReady, onScene, onNewScene, onInfo }) {
  const [view, setView] = useState(null);
  const keys = giraffe.g360_scenes || [];
  const live = useRef({});
  live.current = { onReady, onScene, onNewScene, onInfo, keys };
  const keyStr = keys.join(",");
  useEffect(() => {
    const list = keyStr ? keyStr.split(",").map((id, i) => ({ id, label: `Room ${i + 1}`, index: i })) : [];
    live.current.onReady?.({ giraffe: true, setMediaByIndex: (i) => { const k = list[i]?.id; if (k) setView(k); } }, list);
  }, [keyStr]);
  useEffect(() => {
    const onMsg = (e) => {
      if (!fromGiraffe(e.origin) || !e.data || typeof e.data !== "object") return;
      const d = e.data;
      const L = live.current;
      if (d.type === "g360:scene-change" && d.sceneKey) {
        const i = L.keys.indexOf(d.sceneKey);
        if (i < 0) L.onNewScene?.(d.sceneKey);
        L.onScene?.(i, { id: d.sceneKey, label: i >= 0 ? `Room ${i + 1}` : "", index: i });
      }
      if (d.type === "g360:info-hotspot-click") L.onInfo?.(d);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="tdv-stage">
      <iframe src={withView(src, view)} title="Giraffe360 tour" className="absolute inset-0 w-full h-full border-0" allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer; autoplay; vr" data-testid="tdv-frame" />
    </div>
  );
}

export function TdvStage(p) {
  if (p.giraffe?.g360_project_id) return <PanoStage key={p.giraffe.g360_project_id} {...p} />;
  return p.giraffe ? <GiraffeStage {...p} /> : <TdvStage3d {...p} />;
}

function TdvStage3d({ src, hotspots = [], native = true, hiddenNative = [], placing = false, onReady, onScene, onPlace, onPin, activeId, numbers = {} }) {
  const frame = useRef(null);
  const tourRef = useRef(null);
  const pins = useRef({});
  const sceneRef = useRef({ index: -1, id: "" });
  const live = useRef({ hotspots, onScene, onReady });
  live.current = { hotspots, onScene, onReady };

  useEffect(() => {
    let raf;
    let wait;
    const loop = () => {
      const t = tourRef.current;
      if (t) {
        const i = currentIndex(t);
        if (i !== sceneRef.current.index) {
          const sc = scenesOf(t)[i];
          sceneRef.current = { index: i, id: sc?.id || "" };
          live.current.onScene?.(i, sc);
        }
        const pp = panoPlayer(t);
        live.current.hotspots.forEach((h) => {
          const el = pins.current[h.id];
          if (!el) return;
          const s = pp && h.media_id === sceneRef.current.id ? project(pp, h.yaw, h.pitch) : null;
          el.style.display = s ? "flex" : "none";
          if (s) el.style.transform = `translate(${s.x}px, ${s.y}px) translate(-50%, -50%)`;
        });
      }
      raf = requestAnimationFrame(loop);
    };
    const poll = () => {
      const t = tdvOf(frame.current);
      if (t && scenesOf(t).length) {
        tourRef.current = t;
        live.current.onReady?.(t, scenesOf(t));
      } else wait = setTimeout(poll, 400);
    };
    poll();
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); clearTimeout(wait); tourRef.current = null; };
  }, [src]);

  const hiddenKey = hiddenNative.join(",");
  useEffect(() => {
    const id = setInterval(() => { if (tourRef.current) { setNativeHotspots(tourRef.current, native, hiddenKey ? hiddenKey.split(",") : []); clearInterval(id); } }, 400);
    return () => clearInterval(id);
  }, [native, src, hiddenKey]);

  const place = (e) => {
    const pp = tourRef.current && panoPlayer(tourRef.current);
    if (!pp) return;
    const r = e.currentTarget.getBoundingClientRect();
    onPlace?.({ media_id: sceneRef.current.id, ...unproject(pp, e.clientX - r.left, e.clientY - r.top, r.width, r.height) });
  };

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="tdv-stage">
      <iframe ref={frame} src={src} title="3DVista tour" className="absolute inset-0 w-full h-full border-0" allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer; autoplay" data-testid="tdv-frame" />
      <div className="absolute inset-0 pointer-events-none">
        {hotspots.map((h) => (
          <button key={h.id} ref={(el) => { pins.current[h.id] = el; }} type="button" onClick={() => onPin?.(h)}
            className={`tdv-pin absolute left-0 top-0 hidden pointer-events-auto items-center justify-center transition-opacity ${pinClass(h)} ${activeId === h.id ? "ring-4 ring-white rounded-full" : ""}`}
            style={pinStyle(h)} title={h.title} data-testid={`tdv-pin-${h.id}`}>
            <PinFace h={h} />
            {numbers[h.id] && <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-white text-slate-900 text-[11px] font-bold grid place-items-center shadow" data-testid={`tdv-pin-number-${h.id}`}>{numbers[h.id]}</span>}
          </button>
        ))}
      </div>
      {placing && <div className="absolute inset-0 cursor-crosshair bg-black/10" onClick={place} data-testid="tdv-place-layer" />}
    </div>
  );
}
